// ManagementHostCard — the console's own host, rendered with fields specific
// to its role (CA endpoint, cert fingerprint, SSO enrollment) rather than the
// generic worker fields (Public IP/Policy/Cloud/Region/Instance/Kernel)
// HostCard shows. Built from the ca/sso/connect blocks /api/fleet returns
// (the same data Identity renders) plus the matching Host record's `name`.
//
// Originally a SYNTHETIC stand-in for a frame where fleetd didn't report
// this host at all — no real telemetry existed then, so none was shown here
// (fabricating CPU/mem/uptime would've violated the same "never fabricate a
// metric" rule the SAMPLE-data fallback follows elsewhere on this page).
// fleetd now reports this host for real (2026-09-10), with real telemetry
// (cpuPct/memPct/uptime/enforcing/kernel/publicIp) — that data just isn't
// surfaced on this card yet; it's identity/enrollment-focused by design, not
// by data availability. A metrics row is a follow-up if this card should
// also double as this host's HostCard-equivalent operational view.
//
// Same visual language as HostCard's former control-plane branch (Lock tag,
// disabled management menu, "can't be destroyed" copy, Identity link) —
// deliberately its own component rather than a HostCard branch, since the
// two show different field sets for the same underlying host.
import { Button, Divider, Flex, Popover, Space, Typography, theme } from "antd";
import { Link } from "react-router-dom";
import { ArrowUpRight, Info, Lock } from "lucide-react";
import { CustomCard, Tag } from "@/shared/ui";
import { parseEnrollmentUrl } from "@/components/fleet/ConnectModal";

const { Text, Title } = Typography;
const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";

export interface ManagementHostCa {
  name?: string;
  host?: string;
  endpoint?: string;
  fp?: string;
}
export interface ManagementHostSso {
  provider?: string;
}

export interface ManagementHostCardProps {
  /** The control-plane host's real reported name (Host.name from /api/fleet,
   *  e.g. "brdemo-01") — NOT the same as its role ("control-plane"). Falls
   *  back to the literal "control-plane" only for the rare frame where the
   *  host list hasn't loaded yet but ca has (ca/sso/connect and hosts come
   *  from the same /api/fleet payload, so in steady state this is always
   *  set). See the git history on this file for why the title used to be a
   *  hardcoded "control-plane" literal — that was true back when this card
   *  was purely synthetic and no real Host record with its own name existed
   *  yet to diverge from that string. */
  name?: string;
  ca: ManagementHostCa;
  sso?: ManagementHostSso;
  /** The fleet's SSO enrollment URL (connect.bootstrap_url) — the same
   *  value ConnectModal hands an admin to relay to an end user. Also the
   *  SOURCE of the full Fingerprint value below — ca.fp itself comes back
   *  from /api/fleet pre-truncated by the backend ("a304e12d02a0…", 13
   *  chars, ellipsis baked into the string), so it's derived from this URL
   *  instead (see parseEnrollmentUrl's doc comment for how that was found). */
  bootstrapUrl?: string;
}

export function ManagementHostCard({ name, ca, sso, bootstrapUrl }: ManagementHostCardProps) {
  const { token } = theme.useToken();
  // Prefer the full fingerprint derived from the enrollment URL; ca.fp is
  // only a fallback for the (rare) case SSO isn't configured yet and no
  // bootstrap URL exists at all.
  const fullFp = (bootstrapUrl && parseEnrollmentUrl(bootstrapUrl).fingerprint) || ca.fp;

  const description =
    "This is your centralized management host — it runs the console, the fleet " +
    "manager, and the certificate authority that issues developer SSH certs. It " +
    "can't be deleted or destroyed here, and no sandboxes are scheduled on it.";

  // No size="small" on the Card below — that drops AntD Card's body padding
  // to 12px; the user asked for the card's real default padding (24px) instead.
  return (
    <CustomCard>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%", gap: 8 }}>
          {/* A plain flex row, not <Space> — <Space> wraps each child in its own
              flex item div, so flexShrink/minWidth set on the CHILDREN (as opposed
              to that wrapper) has no effect. A long real hostname would then force
              the Tag's own box to shrink and its text to wrap. Here the hostname
              is the one element that's allowed to shrink (and ellipsize); the icon,
              Tag, and info icon are pinned via flexShrink: 0 on the actual flex
              items. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: "1 1 auto" }}>
            {/* The host's real reported name (Host.name, e.g. "brdemo-01") —
                NOT the "control-plane" role/Tag label to the right of it.
                Same mono treatment as host.name on a worker card. */}
            {/* level={3} — with the page's own title a real <h1> (see corenet's
                PageHeader) and the "Hosts" section heading below the KPI row a
                real <h2>, this card's title is a sub-section of that <h2>, same
                as HostCard's, so <h3> is the correct outline depth. */}
            <Title level={3} style={{ margin: 0, fontFamily: MONO, fontSize: 14, fontWeight: 600 }}>
              {name || "control-plane"}
            </Title>
            <Tag
              // No color prop — a neutral/default tag, matching the "worker" tag's
              // own grey elsewhere in this card. This isn't a status/severity signal
              // (blue/"processing" implied "in progress"), it's just a role label.
              // className, not style: AntD Tag's cloneElement on `icon` REPLACES style
              // entirely (`style: mergedStyles.icon`, antd/es/tag/index.js) but merges
              // className via clsx — an inline style here is silently discarded. The
              // Tailwind utility overrides Tailwind's own preflight `svg { display: block }`;
              // without it the block-level icon pushes the tag's text onto its own line.
              icon={<Lock size={11} className="inline-block align-middle mr-1.5" />}
              mono
              style={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Management Host
            </Tag>
            <Popover content={<div style={{ maxWidth: 320 }}>{description}</div>} placement="bottom">
              <Info size={13} strokeWidth={1.75} style={{ flexShrink: 0, cursor: "help", color: token.colorTextTertiary }} />
            </Popover>
          </div>
          {/* No management menu here — unlike a worker host, this one has nothing
              this UI can ever do to it (see the Tag's Popover: it can't be resized
              or removed through the console at all, only from the CLI on the box
              itself), so a kebab menu that always opens to one disabled row isn't
              a real affordance. */}
        </div>

        {/* Same full-width rule as HostCard, separating the identity row from
            the metadata below — kept consistent across every card on the page. */}
        <Divider style={{ margin: 0 }} />

        {/* Labeled fact grid, not a dot-joined line — "step-ca · endpoint · fp hash…"
            crammed three unrelated kinds of information (a software name, a network
            endpoint, a crypto fingerprint) into one dense mono sentence. Split into
            labeled cells so each fact is scannable on its own, matching the same
            label-above-value pattern the fleet-worker card below uses. SSO folded in
            as a third cell rather than a separate line below — it's the same kind of
            labeled fact as the other two. Cells are each independently conditional
            (unlike the old single `ca.endpoint &&` gate) so the SSO cell keeps
            rendering even on the rare snapshot missing a CA endpoint, matching the
            unconditional SSO link this replaces. auto-fit + minmax floor, not a
            fixed repeat(4, ...) count — columns that can't fit at >=110px wrap
            onto their own row instead of compressing or forcing a scrollbar. */}
        {/* columnGap only (+50%), rowGap unchanged — matches HostCard's fact
            grid, "between-column padding" means horizontal spacing. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", rowGap: token.padding, columnGap: token.padding * 1.5 }}>
          {ca.endpoint && (
            // minWidth: 0 — a CSS grid item's default min-width: auto lets the cell
            // expand to fit the full un-truncated text, which throws off AntD's own
            // truncated-or-not measurement for the ellipsis tooltip (same bug already
            // hit and fixed on the Fingerprint cell below).
            <div style={{ minWidth: 0 }}>
              <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                CA endpoint
              </Text>
              <div style={{ fontSize: 13, fontFamily: MONO }}>
                <Text
                  style={{ fontFamily: MONO, fontSize: 13, display: "block" }}
                  ellipsis={{ tooltip: ca.endpoint }}
                  copyable={{ text: ca.endpoint }}
                >
                  {ca.endpoint}
                </Text>
              </div>
            </div>
          )}
          {fullFp && (
            <div style={{ minWidth: 0 }}>
              <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                Fingerprint
              </Text>
              {/* Full value as children with AntD's own ellipsis, not a hardcoded
                  16-char slice — an earlier version's Tooltip claimed to reveal the
                  full fp on hover but showed the same truncated text back (that slice
                  was baked into what got passed as children AND as the tooltip
                  title). Fixed then by switching to ellipsis={{tooltip}} — but the
                  SAME symptom came back later via a different root cause: ca.fp
                  itself is pre-truncated by the backend, so ellipsis had nothing
                  more to reveal no matter how it was wired. fullFp (derived from
                  the enrollment URL — see parseEnrollmentUrl) is the real value. */}
              <div style={{ fontSize: 13, fontFamily: MONO }}>
                <Text
                  style={{ fontFamily: MONO, fontSize: 13, display: "block" }}
                  ellipsis={{ tooltip: fullFp }}
                  copyable={{ text: fullFp }}
                >
                  {fullFp}
                </Text>
              </div>
            </div>
          )}
          {bootstrapUrl && (
            <div style={{ minWidth: 0 }}>
              <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                Enrollment URL
              </Text>
              <div style={{ fontSize: 13, fontFamily: MONO }}>
                <Text
                  style={{ fontFamily: MONO, fontSize: 13, display: "block" }}
                  ellipsis={{ tooltip: bootstrapUrl }}
                  copyable={{ text: bootstrapUrl }}
                >
                  {bootstrapUrl}
                </Text>
              </div>
            </div>
          )}
          <div>
            <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
              SSO provider
            </Text>
            {/* Provider name is plain text — the value itself isn't the
                action. "Manage" is its own Button type="link" CTA, matching
                the "Manage providers on Identity" language already used in
                useFleetOps.tsx's SsoDrawer. wrap + nowrap on the provider
                name: without it, both flex children shrink together and
                "Microsoft Entra ID" itself started breaking mid-phrase
                before the (lower-priority) Manage Identity link gave up its
                spot on the row — now the link is what drops to its own
                line first, the provider name never breaks internally. */}
            <Flex align="center" gap={6} wrap="wrap">
              <Text style={{ fontSize: 13, whiteSpace: "nowrap" }}>{sso?.provider ?? "Not configured"}</Text>
              <Link to="/identity">
                <Button type="link" size="small" style={{ display: "flex", alignItems: "center", gap: 4, padding: 0, height: "auto", fontSize: 13, whiteSpace: "nowrap" }}>
                  Manage Identity
                  <ArrowUpRight size={11} style={{ opacity: 0.5 }} />
                </Button>
              </Link>
            </Flex>
          </div>
        </div>
      </Space>
    </CustomCard>
  );
}

export default ManagementHostCard;
